import * as React from 'react'

export type ProgressTone = 'brand' | 'danger'

export interface ProgressProps {
  /**
   * Completed fraction, 0–100. Clamped into range and rounded, so a raw API percent
   * (or a slightly-out-of-range value) is safe to pass straight through.
   */
  value: number;
  /**
   * Accessible name for the bar (e.g. "Quota used"). Set it whenever there isn't a
   * visible label already associated with the bar — it becomes the progressbar's
   * `aria-label`.
   */
  label?: string;
  /**
   * Colour treatment. `brand` (default) for ordinary consumption, `danger` when the
   * value has crossed a warning threshold the caller decides (e.g. quota nearly
   * exhausted).
   */
  tone?: ProgressTone;
  className?: string;
}

/**
 * A horizontal consumption bar — quota used, upload progress, and the like. A
 * div-based bar (not the native `<progress>`, which the old TV browsers this UI
 * targets style inconsistently) carrying `role="progressbar"` and the aria value
 * attributes so it is announced correctly. Sizes to its container's width.
 */
export function Progress ({ value, label, tone = 'brand', className }: ProgressProps) {
  const pct = Math.max(0, Math.min(100, Math.round(value)))
  const classes = ['mlsg-progress', `mlsg-progress--${tone}`, className].filter(Boolean).join(' ')
  return (
    <div
      className={classes}
      role='progressbar'
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className='mlsg-progress__fill' style={{ width: `${pct}%` }} />
    </div>
  )
}
